import { Image } from '@tiptap/extension-image';
import createImageNodeView from '@/components/content/tiptap/createImageNodeView';

/**
 * Supported image layout modes, split into two families.
 *
 * Wrap family — the image floats and surrounding text flows beside it:
 * - inline:     flows with text (matches base inline image behaviour)
 * - left-wrap:  floats left, text wraps on the right
 * - right-wrap: floats right, text wraps on the left
 *
 * No-wrap family — the image sits alone on its line:
 * - left:    block, flush left
 * - center:  block, horizontally centered
 * - right:   block, flush right
 */
export const IMAGE_LAYOUTS = ['inline', 'left-wrap', 'right-wrap', 'left', 'center', 'right'];

export const IMAGE_LAYOUT_BUTTONS = [
  { layout: 'inline', icon: 'mdi-image-text', title: 'Inline' },
  { layout: 'left-wrap', icon: 'mdi-format-float-left', title: 'Wrap left' },
  { layout: 'right-wrap', icon: 'mdi-format-float-right', title: 'Wrap right' },
  { layout: 'left', icon: '$i-image-align-left', title: 'Left' },
  { layout: 'center', icon: '$i-image-align-center', title: 'Center' },
  { layout: 'right', icon: '$i-image-align-right', title: 'Right' }
];

export const ALIGN_ONLY_IMAGE_LAYOUTS = ['left', 'center', 'right'];

/** Parse a numeric pixel width from a `width` attribute (e.g. "320" or "320px"). */
const parseWidth = (value) => {
  if (value == null || value === '') return null;
  const width = parseInt(value, 10);
  return Number.isNaN(width) ? null : width;
};

/**
 * Extends the base Tiptap Image node with a persisted `layout` attribute so
 * images can be aligned/wrapped. The layout is stored on the rendered `<img>`
 * as a `data-layout` attribute (CSS targets `img[data-layout]`). We deliberately
 * do NOT touch the `class` attribute, so the app's `client-private-image` class
 * (used for image src resolution / lazy-loading) is preserved.
 */
const ImageWithLayout = Image.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      // `null` means "no layout ever chosen" — content authored before this
      // feature existed. Such images must round-trip byte-identically: emitting
      // a default `data-layout` here would stamp every legacy image on a page
      // the first time an author saves it, silently re-flowing images they
      // never touched and churning the content-engine git history.
      // `null` is therefore distinct from an explicit `inline` choice.
      layout: {
        default: null,
        parseHTML: (element) => element.getAttribute('data-layout'),
        renderHTML: (attributes) => (attributes.layout ? { 'data-layout': attributes.layout } : {})
      },
      // Persisted as the HTML `width` attribute (px) so resized images survive
      // the markdown save/load pipeline. `null` means "natural width".
      width: {
        default: null,
        parseHTML: (element) => parseWidth(element.getAttribute('width')),
        renderHTML: (attributes) => (attributes.width ? { width: `${attributes.width}` } : {})
      },
      // Preserve the lazy-load hash through edits so it is never lost from the
      // node model (the save pipeline also re-derives it from `alt`).
      'data-filename-hash': {
        default: null,
        parseHTML: (element) => element.getAttribute('data-filename-hash'),
        renderHTML: (attributes) =>
          attributes['data-filename-hash']
            ? { 'data-filename-hash': attributes['data-filename-hash'] }
            : {}
      }
    };
  },

  addCommands() {
    return {
      ...this.parent?.(),
      setImageLayout:
        (layout) =>
        ({ commands }) => {
          if (!IMAGE_LAYOUTS.includes(layout)) return false;
          return commands.updateAttributes(this.name, { layout });
        }
    };
  },

  addNodeView() {
    return createImageNodeView();
  }
});

export default ImageWithLayout;
